// The plan card names the product the org has. No Stripe subscription is
// not the Free plan — Free is billing.freeEntitlements. A grant past the
// richest self-serve plan is Enterprise. Whether we invoice the org stays
// off this label.

const ACTIVE = new Set(['active', 'trialing', 'past_due']);

function covers(have, need) {
  return Object.entries(need || {}).every(([k, v]) => {
    const got = have?.[k];
    if (typeof v === 'boolean') return v === false || got === true;
    if (typeof v === 'number') return Number(got) >= v;
    return got === v;
  });
}

function sameEntitlements(a, b) {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const k of keys) if ((a || {})[k] !== (b || {})[k]) return false;
  return true;
}

// `have` already covers `need`. True when at least one cap is higher.
function exceeds(have, need) {
  if (!covers(have, need)) return false;
  const keys = new Set([...Object.keys(have || {}), ...Object.keys(need || {})]);
  for (const k of keys) {
    const got = have?.[k];
    const cap = need?.[k];
    if (got === cap) continue;
    if (typeof got === 'number' || typeof cap === 'number') {
      if (Number(got) > Number(cap || 0)) return true;
    } else if (got === true && cap !== true) return true;
  }
  return false;
}

// The plan every other catalog plan fits inside. Incomparable rungs
// (Kit Site vs Start) lose to the one that covers them (Business).
function richestPlan(plans) {
  let best = null;
  for (const [key, plan] of Object.entries(plans || {})) {
    const entitlements = plan?.entitlements || {};
    if (!best || (covers(entitlements, best.entitlements) && exceeds(entitlements, best.entitlements))) {
      best = { key, name: plan?.name || key, entitlements };
    }
  }
  return best;
}

function exactPlan(plans, ents) {
  for (const [key, plan] of Object.entries(plans || {})) {
    if (sameEntitlements(ents, plan?.entitlements || {})) return plan?.name || key;
  }
  return null;
}

// plans === null means the catalog has not loaded — don't guess Free.
export function displayedOrgPlan(org, { plans = null, freeEntitlements = null } = {}) {
  const sub = org?.subscription || {};
  if (ACTIVE.has(sub.status) && (sub.planName || sub.planKey)) {
    return { name: sub.planName || sub.planKey, status: sub.status, selfServe: true };
  }

  const ents = org?.entitlements || {};
  const free = !Object.keys(ents).length
    || (freeEntitlements != null && sameEntitlements(ents, freeEntitlements));
  if (plans == null) {
    return free
      ? { name: 'Free', status: 'no subscription', selfServe: true }
      : { name: '', status: null, selfServe: false };
  }
  if (free) return { name: 'Free', status: 'no subscription', selfServe: true };

  const top = richestPlan(plans);
  if (top && covers(ents, top.entitlements) && exceeds(ents, top.entitlements)) {
    return { name: 'Enterprise', status: null, selfServe: false };
  }
  const matched = exactPlan(plans, ents);
  if (matched) return { name: matched, status: null, selfServe: true };
  return { name: 'Free', status: 'no subscription', selfServe: true };
}
